import { createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from "react";
import { developerAvatarSrc, type Developer } from "../developer/api";
import { IconBack, IconProfile } from "./icons";

type HeaderActionCtx = {
  action: ReactNode;
  setAction: (node: ReactNode) => void;
};

const HeaderActionContext = createContext<HeaderActionCtx>({
  action: null,
  setAction: () => {},
});

export function HeaderActionProvider({ children }: { children: ReactNode }) {
  const [action, setAction] = useState<ReactNode>(null);
  const value = useMemo(() => ({ action, setAction }), [action]);
  return <HeaderActionContext.Provider value={value}>{children}</HeaderActionContext.Provider>;
}

export function useHeaderAction(node: ReactNode) {
  const { setAction } = useContext(HeaderActionContext);
  useLayoutEffect(() => {
    setAction(node);
    return () => setAction(null);
  }, [node, setAction]);
}

export function mobileTitle(path: string): string {
  if (path === "/cameras") return "Cameras";
  if (path === "/playback") return "Playback";
  if (path === "/live-trip") return "Live Trip";
  if (path === "/users" || path.startsWith("/users/")) return "Users";
  if (path === "/sdk") return "SDK";
  if (path === "/settings") return "Settings";
  if (path === "/settings/password") return "Change password";
  if (path === "/settings/email") return "Change email";
  if (path === "/settings/smtp") return "SMTP / Email Configuration";
  if (path === "/profile") return "Profile";
  if (path === "/developer/web-sdk" || path === "/developer/sdk") return "Web SDK";
  if (path === "/developer/flutter-sdk") return "Flutter SDK";
  if (path === "/developer/api-keys") return "API Keys";
  if (path === "/developer/docs") return "API Documentation";
  if (path === "/developer/examples") return "Integration examples";
  if (path === "/developer" || path === "/developer/overview") return "Developer";
  if (path === "/dev") return "Dev logs";
  return "Home";
}

export function mobileBackTo(path: string): string | null {
  if (path === "/settings/password" || path === "/settings/email" || path === "/settings/smtp") return "/settings";
  if (path === "/playback") return "/cameras";
  if (path === "/live-trip") {
    return new URLSearchParams(window.location.search).get("from") === "cameras" ? "/cameras" : "/";
  }
  if (path === "/developer/web-sdk" || path === "/developer/flutter-sdk" || path === "/developer/sdk") return "/sdk";
  if (path.startsWith("/developer") || path === "/dev") return "/settings";
  return null;
}

export function MobileHeader({
  path,
  go,
  me,
}: {
  path: string;
  go: (to: string) => void;
  me?: Developer | null;
}) {
  const { action } = useContext(HeaderActionContext);
  const back = mobileBackTo(path);
  const home = path === "/" || path === "/dashboard";
  const cameras = path === "/cameras";
  const avatarSrc = me ? developerAvatarSrc(me) : null;

  return (
    <header className={`mobile-header${home ? " is-home" : ""}${cameras ? " is-cameras" : ""}`}>
      {home ? (
        <>
          <div className="mobile-brand">
            <img src="/logo.png" alt="" />
            <strong>Dashcam</strong>
          </div>
          <button type="button" className="header-icon-btn header-avatar-btn" onClick={() => go("/profile")} aria-label="Profile">
            {avatarSrc ? <img src={avatarSrc} alt="" /> : <IconProfile />}
          </button>
        </>
      ) : (
        <>
          {back ? (
            <button
              type="button"
              className="mobile-back"
              onClick={() => {
                if (path === "/live-trip" && window.history.length > 1) {
                  window.history.back();
                  return;
                }
                go(back);
              }}
              aria-label="Back"
            >
              <IconBack />
            </button>
          ) : null}
          <h1>{mobileTitle(path)}</h1>
          {action ?? <span className="mobile-header-spacer" aria-hidden="true" />}
        </>
      )}
    </header>
  );
}
